import { Option } from '@components/Select/SelectTsoPq';
import { TsosStateProps } from '@pages/MeasurementVisualization/utils';
import {
  ApiApplicationServicesDataTypesCorrelationTypesCorrelation,
  ApiApplicationServicesDataTypesCorrelationTypesCorrelationCount,
} from '@redux/apiSlices/correlationApi';
import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';

type ParameterType = 'single' | 'multiple' | '';

export interface Parameters {
  calculationName: string;
  activeTsos: TsosStateProps[] | [];
  start: string | undefined;
  end: string | undefined;
  deviceTechnicalNames: { [key: string]: string[] };
  parameterType: ParameterType;
  type: number;
  selectedParameters: string[];
  threshold: string | undefined;
}

export interface CorrelationState {
  selectedTso: string;
  activeTsos: TsosStateProps[] | [];
  activeTsoName: string;
  originalCalculationParameters: Parameters;
  activeCalculationParameters: Parameters;
  isLoading: boolean;
  preselectedCalculationId: number;
  selectedCalculation: Option | null | undefined;
  triedSubmitting: boolean;
  deviceLoaded: boolean;
  mandatoryFieldsStatus: {
    tso: boolean | undefined;
    start: boolean | undefined;
    end: boolean | undefined;
    threshold: number | undefined;
    selectedDevices: { [key: string]: boolean | undefined };
    selectedParameters: string[] | undefined;
  };
  singleCoefficients: ApiApplicationServicesDataTypesCorrelationTypesCorrelation[]; // type 0
  correlationCounts: ApiApplicationServicesDataTypesCorrelationTypesCorrelationCount[];
}

const initialState: CorrelationState = {
  selectedTso: '',
  activeTsos: [],
  activeTsoName: '',
  activeCalculationParameters: {
    calculationName: '',
    activeTsos: [],
    start: undefined,
    end: undefined,
    deviceTechnicalNames: {},
    parameterType: 'single',
    type: 0,
    selectedParameters: [],
    threshold: undefined,
  },
  originalCalculationParameters: {
    calculationName: '',
    activeTsos: [],
    start: undefined,
    end: undefined,
    deviceTechnicalNames: {},
    parameterType: 'single',
    type: 0,
    selectedParameters: [],
    threshold: undefined,
  },
  isLoading: false,
  preselectedCalculationId: Number.NaN,
  selectedCalculation: undefined,
  triedSubmitting: false,
  deviceLoaded: false,
  mandatoryFieldsStatus: {
    tso: undefined,
    start: undefined,
    end: undefined,
    threshold: undefined,
    selectedDevices: {},
    selectedParameters: undefined,
  },
  singleCoefficients: [],
  correlationCounts: [],
};

const cloneCalculationParameters = (
  params: CorrelationState['activeCalculationParameters']
): CorrelationState['activeCalculationParameters'] => ({
  ...params,
  activeTsos: [...(params.activeTsos ?? [])].map((tso) => ({ ...tso })),
  deviceTechnicalNames: Object.fromEntries(
    Object.entries(params.deviceTechnicalNames || {}).map(([key, value]) => [key, [...(value ?? [])]])
  ),
  selectedParameters: [...(params.selectedParameters ?? [])],
});

const correlationSlice = createSlice({
  name: 'correlation',
  initialState,
  reducers: {
    setActiveTsos: (state, action: PayloadAction<TsosStateProps[]>) => {
      state.activeCalculationParameters.activeTsos = action.payload;
    },
    setStart: (state, action: PayloadAction<string>) => {
      state.activeCalculationParameters.start = action.payload;
    },
    setEnd: (state, action: PayloadAction<string>) => {
      state.activeCalculationParameters.end = action.payload;
    },
    setDeviceTechnicalNames: (state, action: PayloadAction<{ [key: string]: string[] }>) => {
      state.activeCalculationParameters.deviceTechnicalNames = { ...action.payload };
    },
    setActiveTsoName: (state, action: PayloadAction<string>) => {
      state.activeTsoName = action.payload;
    },
    setSelectedParameters: (state, action: PayloadAction<string[]>) => {
      state.activeCalculationParameters.selectedParameters = action.payload;
    },
    setType: (state, action: PayloadAction<number>) => {
      state.activeCalculationParameters.type = action.payload;
    },
    setThreshold: (state, action: PayloadAction<string | undefined>) => {
      state.activeCalculationParameters.threshold = action.payload;
    },
    setCalculationName: (state, action: PayloadAction<string>) => {
      state.activeCalculationParameters.calculationName = action.payload;
    },

    setSelectedCalculation: (state, action: PayloadAction<Option | null | undefined>) => {
      state.selectedCalculation = action.payload;
    },
    setParameterSelection: (state, action: PayloadAction<'single' | 'multiple' | ''>) => {
      state.activeCalculationParameters.parameterType = action.payload;
    },
    resetAll: () => initialState,
    setTriedSubmitting: (state, action: PayloadAction<boolean>) => {
      state.triedSubmitting = action.payload;
    },
    setMandatoryFieldsStatus: (state, action: PayloadAction<CorrelationState['mandatoryFieldsStatus']>) => {
      state.mandatoryFieldsStatus = action.payload;
    },

    setCalculationParameters: (state, action: PayloadAction<CorrelationState['activeCalculationParameters']>) => {
      state.originalCalculationParameters = cloneCalculationParameters(action.payload);
      state.activeCalculationParameters = cloneCalculationParameters(action.payload);
    },
    setSingleCoefficients: (
      state,
      action: PayloadAction<ApiApplicationServicesDataTypesCorrelationTypesCorrelation[]>
    ) => {
      state.singleCoefficients = action.payload;
    },
    setCorrelationCounts: (
      state,
      action: PayloadAction<ApiApplicationServicesDataTypesCorrelationTypesCorrelationCount[]>
    ) => {
      state.correlationCounts = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
  },
});

export const {
  setActiveTsos,
  setDeviceTechnicalNames,
  setEnd,
  setStart,
  setActiveTsoName,
  setSelectedCalculation,
  resetAll,
  setMandatoryFieldsStatus,
  setTriedSubmitting,
  setParameterSelection,
  setSelectedParameters,
  setCalculationName,
  setType,
  setThreshold,
  setCalculationParameters,
  setCorrelationCounts,
  setSingleCoefficients,
  setIsLoading,
} = correlationSlice.actions;

export const selectActiveTsos = (state: { correlation: CorrelationState }) =>
  state.correlation.activeCalculationParameters.activeTsos;

export const selectSelectedCalculation = (state: { correlation: CorrelationState }) =>
  state.correlation.selectedCalculation;

export const selectIsLoading = (state: { correlation: CorrelationState }) => state.correlation.isLoading;

export const selectTriedSubmitting = (state: { correlation: CorrelationState }) => state.correlation.triedSubmitting;

export const validateMandatoryFields = (parameters: {
  calculationName: string;
  activeTsos: TsosStateProps[];
  start: string | undefined;
  end: string | undefined;
  deviceTechnicalNames: { [key: string]: string[] };
  parameterType: 'single' | 'multiple' | '';
  type: number;
  selectedParameters: string[];
  threshold: string | undefined;
}) => {
  const { activeTsos, start, end, threshold, deviceTechnicalNames, selectedParameters, parameterType } = parameters;

  const isStrictFloat = (threshold: string): boolean => {
    if (threshold === '') return false;
    const number = Number(threshold);
    return !Number.isNaN(number) && Number.isFinite(number);
  };

  const tsoValid = activeTsos.length > 0;
  const startValid = Boolean(start);
  const endValid = Boolean(end);
  const selectedParametersValid = selectedParameters.length > 0;

  const thresholdRequired = parameterType === 'multiple';
  const thresholdValid = thresholdRequired ? isStrictFloat(threshold ?? '') : true;

  const selectedDevicesStatus = Object.fromEntries(
    Object.entries(deviceTechnicalNames).map(([key, devices]) => [key, devices.length > 0 || undefined])
  );

  const allDevicesValid = Object.values(selectedDevicesStatus).every(Boolean);

  const allValid = tsoValid && startValid && endValid && thresholdValid && selectedParametersValid && allDevicesValid;

  const mandatoryFieldsStatus = {
    tso: tsoValid || undefined,
    start: startValid || undefined,
    end: endValid || undefined,
    threshold: thresholdValid ? 1 : undefined,
    selectedDevices: selectedDevicesStatus,
    selectedParameters: selectedParametersValid ? selectedParameters : undefined,
  };

  return { mandatoryFieldsStatus, allValid };
};

export const selectActiveCalculationParameters = (state: { correlation: CorrelationState }) =>
  state.correlation.activeCalculationParameters;

export const selectOriginalCalculationParameters = (state: { correlation: CorrelationState }) =>
  state.correlation.originalCalculationParameters;

export const selectIsDirty = createSelector(
  [selectOriginalCalculationParameters, selectActiveCalculationParameters],
  (original, current) => {
    if (!original || !current) return false;
    return JSON.stringify(original) !== JSON.stringify(current);
  }
);

export const selectSingleCoefficients = (state: { correlation: CorrelationState }) =>
  state.correlation.singleCoefficients;

export const selectCorrelationCounts = (state: { correlation: CorrelationState }) =>
  state.correlation.correlationCounts;

export default correlationSlice.reducer;
